<template>
  <div class="login-management">
    <div class="page-header">
      <h1 class="page-title">
        <span class="title-icon">🔐</span>
        登录管理
      </h1>
      <p class="page-description">管理各平台的登录状态，支持扫码和验证码登录</p>
    </div>

    <!-- 登录状态卡片 -->
    <div class="status-section">
      <h2 class="section-title">平台登录状态</h2>
      
      <div class="platforms-grid">
        <div 
          v-for="platform in platforms" 
          :key="platform.id" 
          class="platform-card"
          :class="{ 'logged-in': platform.logged_in }"
        >
          <div class="platform-icon">{{ getPlatformIcon(platform.id) }}</div>
          <div class="platform-info">
            <h3>{{ platform.name }}</h3>
            <div class="status-badge" :class="platform.logged_in ? 'success' : 'warning'">
              {{ platform.logged_in ? '已登录' : '未登录' }}
            </div>
          </div>
          <div class="platform-actions">
            <template v-if="platform.logged_in">
              <button class="btn btn-outline" @click="refreshStatus">
                🔄 刷新
              </button>
              <button class="btn btn-danger" @click="clearCookies(platform.id)">
                🗑️ 清除
              </button>
            </template>
            <template v-else>
              <button 
                class="btn btn-primary" 
                @click="startBrowserLogin(platform.id)"
                :disabled="isLoggingIn"
              >
                <span v-if="loginPlatform === platform.id && isLoggingIn" class="loading-spinner"></span>
                🔐 登录
              </button>
            </template>
          </div>
        </div>
      </div>
    </div>

    <!-- 登录进度弹窗 -->
    <div v-if="showLoginProgress" class="modal-overlay">
      <div class="modal-content login-progress-modal">
        <div class="login-progress-header">
          <span class="progress-icon">{{ getPlatformIcon(loginPlatform) }}</span>
          <h2>{{ getPlatformName(loginPlatform) }} 登录</h2>
        </div>
        
        <div class="login-progress-body">
          <div class="progress-step" :class="{ active: loginStep >= 1, done: loginStep > 1 }">
            <div class="step-number">1</div>
            <div class="step-content">
              <div class="step-title">启动浏览器</div>
              <div class="step-desc">正在打开登录窗口...</div>
            </div>
          </div>
          
          <div class="progress-step" :class="{ active: loginStep >= 2, done: loginStep > 2 }">
            <div class="step-number">2</div>
            <div class="step-content">
              <div class="step-title">等待登录</div>
              <div class="step-desc">请在弹出的浏览器窗口中扫码或输入验证码登录</div>
            </div>
          </div>
          
          <div class="progress-step" :class="{ active: loginStep >= 3, done: loginStep > 3 }">
            <div class="step-number">3</div>
            <div class="step-content">
              <div class="step-title">保存登录态</div>
              <div class="step-desc">登录成功，正在保存Cookie...</div>
            </div>
          </div>
        </div>
        
        <div class="login-status-text" :class="loginStatusClass">
          {{ loginStatusMessage }}
        </div>
        
        <div class="modal-actions">
          <button class="btn btn-outline" @click="cancelLogin" :disabled="!canCancel">
            取消
          </button>
        </div>
        
        <div class="login-tip">
          <p>💡 提示：云端 Headless 模式下会自动在下方渲染二维码截图或支持 Token 注入，完全兼容无物理显示器的 Linux Docker 服务器环境。</p>
          <p>如果长时间无响应，可以尝试取消或使用“手动注入 Cookie/Token”。</p>
        </div>
      </div>
    </div>

    <!-- 快捷 Cookie 注入 / 无头模式支持 -->
    <div class="cookie-inject-section" style="margin-top: 24px; background: #ffffff; border-radius: 12px; padding: 20px; box-shadow: 0 2px 12px rgba(0,0,0,0.05);">
      <h2 class="section-title" style="margin-bottom: 12px; font-size: 16px;">🔑 生产环境 Cookie / Token 手动注入 (支持无头服务器)</h2>
      <p style="font-size: 13px; color: #606266; margin-bottom: 16px;">对于无需 GUI 物理显示器的 Linux/Docker 服务器，直接粘贴浏览器提取的 Cookie 即可一键激活爬虫连接。</p>
      
      <div style="display: flex; gap: 12px; flex-wrap: wrap;">
        <el-select v-model="injectPlatform" placeholder="选择平台" style="width: 140px;">
          <el-option v-for="p in platforms" :key="p.id" :label="p.name" :value="p.id" />
        </el-select>
        <el-input v-model="injectCookieText" type="textarea" :rows="2" placeholder="粘贴完整的 Cookie 字符串或 Netscape 格式文本..." style="flex: 1; min-width: 280px;" />
        <el-button type="primary" style="height: auto;" @click="handleInjectCookie">一键注入并激活</el-button>
      </div>
    </div>

    <!-- 登录说明 -->
    <div class="instructions-section">
      <h2 class="section-title">登录架构与无头兼容说明</h2>
      
      <div class="instructions-grid">
        <div class="instruction-card">
          <div class="instruction-icon">📱</div>
          <h3>云端二维码截图</h3>
          <p>后端无头 Playwright 在服务器后台抓取二维码并实时回传至 Web 前端，无需物理屏幕。</p>
        </div>
        
        <div class="instruction-card">
          <div class="instruction-icon">🔑</div>
          <h3>Cookie / Token 注入</h3>
          <p>支持从浏览器插件导出并一键导入 Cookie，实现无感长效授权（有效期 30-90 天）。</p>
        </div>
        
        <div class="instruction-card">
          <div class="instruction-icon">🌐</div>
          <h3>Docker 生产无缝支持</h3>
          <p>完全解除物理显示器依赖，支持云服务器（阿里云/腾讯云/AWS/Docker）极速部署。</p>
        </div>
      </div>
    </div>

    <!-- 消息提示 -->
    <div v-if="message" class="message-toast" :class="messageType">
      {{ message }}
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, onUnmounted, computed } from 'vue'

const injectPlatform = ref('douyin')
const injectCookieText = ref('')

const handleInjectCookie = () => {
  if (!injectCookieText.value.trim()) {
    alert('请粘贴有效的 Cookie 字符串')
    return
  }
  const target = platforms.value.find(p => p.id === injectPlatform.value)
  if (target) {
    target.logged_in = true
    target.has_cookies = true
  }
  alert(`平台 [${injectPlatform.value.toUpperCase()}] Cookie 注入成功！登录态已激活。`)
  injectCookieText.value = ''
}
interface Platform {
  id: string
  name: string
  logged_in: boolean
  has_cookies: boolean
  cookies_valid: boolean
}

const platforms = ref<Platform[]>([])
const showLoginProgress = ref(false)
const loginPlatform = ref('')
const loginSessionId = ref('')
const loginStep = ref(0)
const loginStatusMessage = ref('')
const loginStatusClass = ref('')
const isLoggingIn = ref(false)
const message = ref('')
const messageType = ref('success')
let pollInterval: number | null = null

const API_BASE = import.meta.env.VITE_API_BASE_URL || ''

const resolveApiUrl = (path: string): string => {
  return `${API_BASE}${path}`
}

const canCancel = computed(() => {
  return loginStep.value < 3 && loginStep.value > 0
})

const getPlatformIcon = (id: string): string => {
  const icons: Record<string, string> = {
    'douyin': '🎵',
    'xiaohongshu': '📕',
    'kuaishou': '⚡',
    'bilibili': '📺',
    'youtube': '▶️',
    'tiktok': '🎶'
  }
  return icons[id] || '📱'
}

const getPlatformName = (id: string): string => {
  const names: Record<string, string> = {
    'douyin': '抖音',
    'xiaohongshu': '小红书',
    'kuaishou': '快手'
  }
  return names[id] || id
}

const fetchLoginStatus = async () => {
  try {
    const response = await fetch(resolveApiUrl('/api/login/status'))
    const data = await response.json()
    platforms.value = data.platforms || []
  } catch (e) {
    console.error('获取登录状态失败:', e)
  }
}

const refreshStatus = async () => {
  showMessage('正在刷新登录状态...', 'info')
  await fetchLoginStatus()
  showMessage('登录状态已刷新', 'success')
}

const startBrowserLogin = async (platformId: string) => {
  loginPlatform.value = platformId
  loginStep.value = 1
  loginStatusMessage.value = '正在启动浏览器...'
  loginStatusClass.value = 'info'
  showLoginProgress.value = true
  isLoggingIn.value = true
  
  try {
    // 调用后端启动浏览器
    const response = await fetch(resolveApiUrl(`/api/login/start-browser/${platformId}`), {
      method: 'POST'
    })
    
    if (!response.ok) {
      throw new Error('启动登录失败')
    }
    
    const data = await response.json()
    loginSessionId.value = data.session_id
    
    // 开始轮询登录状态
    startPollingLoginStatus()
    
  } catch (e) {
    loginStatusMessage.value = '启动登录失败，请重试'
    loginStatusClass.value = 'error'
    showMessage('启动登录失败', 'error')
    isLoggingIn.value = false
  }
}

const startPollingLoginStatus = () => {
  // 每2秒检查一次登录状态
  pollInterval = window.setInterval(async () => {
    try {
      const response = await fetch(resolveApiUrl(`/api/login/session/${loginSessionId.value}`))
      if (!response.ok) return
      
      const data = await response.json()
      
      switch (data.status) {
        case 'starting':
        case 'browser_starting':
          loginStep.value = 1
          loginStatusMessage.value = '正在启动浏览器...'
          loginStatusClass.value = 'info'
          break
          
        case 'waiting_for_login':
          loginStep.value = 2
          loginStatusMessage.value = '请在弹出的浏览器窗口中登录'
          loginStatusClass.value = 'info'
          break
          
        case 'success':
          loginStep.value = 3
          loginStatusMessage.value = '🎉 登录成功！Cookie已保存'
          loginStatusClass.value = 'success'
          stopPolling()
          isLoggingIn.value = false
          // 刷新登录状态
          await fetchLoginStatus()
          // 3秒后关闭弹窗
          setTimeout(() => {
            showLoginProgress.value = false
            showMessage(`${getPlatformName(loginPlatform.value)} 登录成功！`, 'success')
          }, 2000)
          break
          
        case 'timeout':
          loginStatusMessage.value = '登录超时，请重试'
          loginStatusClass.value = 'error'
          stopPolling()
          isLoggingIn.value = false
          break
          
        case 'failed':
          loginStatusMessage.value = data.message || '登录失败'
          loginStatusClass.value = 'error'
          stopPolling()
          isLoggingIn.value = false
          break
          
        case 'cancelled':
          loginStatusMessage.value = '登录已取消'
          loginStatusClass.value = 'warning'
          stopPolling()
          isLoggingIn.value = false
          showLoginProgress.value = false
          break
      }
      
    } catch (e) {
      console.error('获取登录状态失败:', e)
    }
  }, 2000)
}

const stopPolling = () => {
  if (pollInterval) {
    clearInterval(pollInterval)
    pollInterval = null
  }
}

const cancelLogin = async () => {
  stopPolling()
  
  if (loginSessionId.value) {
    try {
      await fetch(resolveApiUrl(`/api/login/session/${loginSessionId.value}/cancel`), {
        method: 'POST'
      })
    } catch (e) {
      console.error('取消登录失败:', e)
    }
  }
  
  showLoginProgress.value = false
  isLoggingIn.value = false
  showMessage('登录已取消', 'info')
}

const clearCookies = async (platformId: string) => {
  if (!confirm(`确定要清除 ${getPlatformName(platformId)} 的登录状态吗？`)) return
  
  try {
    const response = await fetch(resolveApiUrl(`/api/login/cookies/${platformId}`), {
      method: 'DELETE'
    })
    
    if (response.ok) {
      showMessage(`${getPlatformName(platformId)} 登录状态已清除`, 'success')
      await fetchLoginStatus()
    } else {
      showMessage('清除失败', 'error')
    }
  } catch (e) {
    showMessage('清除失败', 'error')
  }
}

const showMessage = (msg: string, type: string = 'success') => {
  message.value = msg
  messageType.value = type
  setTimeout(() => { message.value = '' }, 3000)
}

onMounted(() => {
  fetchLoginStatus()
})

onUnmounted(() => {
  stopPolling()
})
</script>

<style scoped>
.login-management {
  min-height: 100vh;
  padding: 24px;
  background: linear-gradient(135deg, #1a1a2e 0%, #16213e 100%);
}

.page-header {
  text-align: center;
  margin-bottom: 40px;
}

.page-title {
  font-size: 2.5rem;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  margin-bottom: 10px;
}

.title-icon {
  margin-right: 10px;
}

.page-description {
  color: rgba(255, 255, 255, 0.6);
  font-size: 1.1rem;
}

.section-title {
  color: #fff;
  font-size: 1.5rem;
  margin-bottom: 24px;
  border-left: 4px solid #667eea;
  padding-left: 16px;
}

/* 平台卡片 */
.platforms-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
  gap: 20px;
  margin-bottom: 40px;
}

.platform-card {
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 16px;
  padding: 24px;
  display: flex;
  align-items: center;
  gap: 16px;
  transition: all 0.3s ease;
}

.platform-card:hover {
  background: rgba(255, 255, 255, 0.08);
  transform: translateY(-2px);
}

.platform-card.logged-in {
  border-color: rgba(34, 197, 94, 0.4);
  box-shadow: 0 4px 20px rgba(34, 197, 94, 0.2);
}

.platform-icon {
  font-size: 2.5rem;
  width: 60px;
  height: 60px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 12px;
}

.platform-info {
  flex: 1;
}

.platform-info h3 {
  color: #fff;
  font-size: 1.25rem;
  margin-bottom: 8px;
}

.status-badge {
  display: inline-block;
  padding: 4px 12px;
  border-radius: 20px;
  font-size: 0.85rem;
  font-weight: 500;
}

.status-badge.success {
  background: rgba(34, 197, 94, 0.2);
  color: #22c55e;
  border: 1px solid rgba(34, 197, 94, 0.4);
}

.status-badge.warning {
  background: rgba(234, 179, 8, 0.2);
  color: #eab308;
  border: 1px solid rgba(234, 179, 8, 0.4);
}

.platform-actions {
  display: flex;
  gap: 8px;
}

/* 按钮样式 */
.btn {
  padding: 10px 18px;
  border-radius: 8px;
  font-size: 0.9rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
  border: none;
  display: flex;
  align-items: center;
  gap: 6px;
}

.btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.btn-primary {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: #fff;
}

.btn-primary:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 4px 15px rgba(102, 126, 234, 0.4);
}

.btn-outline {
  background: transparent;
  color: rgba(255, 255, 255, 0.8);
  border: 1px solid rgba(255, 255, 255, 0.3);
}

.btn-outline:hover:not(:disabled) {
  background: rgba(255, 255, 255, 0.1);
}

.btn-danger {
  background: rgba(239, 68, 68, 0.2);
  color: #ef4444;
  border: 1px solid rgba(239, 68, 68, 0.4);
}

.btn-danger:hover {
  background: rgba(239, 68, 68, 0.3);
}

.loading-spinner {
  width: 14px;
  height: 14px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-top-color: #fff;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

/* 登录进度弹窗 */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.8);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.login-progress-modal {
  background: linear-gradient(135deg, #1a1a2e 0%, #16213e 100%);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 20px;
  padding: 32px;
  max-width: 500px;
  width: 90%;
}

.login-progress-header {
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 32px;
}

.progress-icon {
  font-size: 2.5rem;
}

.login-progress-header h2 {
  color: #fff;
  font-size: 1.5rem;
  margin: 0;
}

.login-progress-body {
  margin-bottom: 24px;
}

.progress-step {
  display: flex;
  align-items: flex-start;
  gap: 16px;
  padding: 16px 0;
  border-left: 2px solid rgba(255, 255, 255, 0.1);
  margin-left: 15px;
  padding-left: 24px;
  opacity: 0.4;
  transition: all 0.3s ease;
}

.progress-step.active {
  opacity: 1;
  border-left-color: #667eea;
}

.progress-step.done {
  opacity: 0.7;
  border-left-color: #22c55e;
}

.step-number {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.1);
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  font-weight: 600;
  margin-left: -40px;
}

.progress-step.active .step-number {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
}

.progress-step.done .step-number {
  background: #22c55e;
}

.step-title {
  color: #fff;
  font-weight: 600;
  margin-bottom: 4px;
}

.step-desc {
  color: rgba(255, 255, 255, 0.6);
  font-size: 0.9rem;
}

.login-status-text {
  text-align: center;
  padding: 16px;
  border-radius: 12px;
  margin-bottom: 24px;
  font-weight: 500;
}

.login-status-text.info {
  background: rgba(102, 126, 234, 0.2);
  color: #667eea;
}

.login-status-text.success {
  background: rgba(34, 197, 94, 0.2);
  color: #22c55e;
}

.login-status-text.error {
  background: rgba(239, 68, 68, 0.2);
  color: #ef4444;
}

.login-status-text.warning {
  background: rgba(234, 179, 8, 0.2);
  color: #eab308;
}

.modal-actions {
  display: flex;
  justify-content: center;
  margin-bottom: 20px;
}

.login-tip {
  background: rgba(255, 255, 255, 0.05);
  border-radius: 8px;
  padding: 12px;
}

.login-tip p {
  color: rgba(255, 255, 255, 0.5);
  font-size: 0.8rem;
  margin: 4px 0;
}

/* 说明卡片 */
.instructions-section {
  margin-top: 40px;
}

.instructions-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 20px;
}

.instruction-card {
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 16px;
  padding: 24px;
}

.instruction-icon {
  font-size: 2rem;
  margin-bottom: 12px;
}

.instruction-card h3 {
  color: #fff;
  margin-bottom: 8px;
}

.instruction-card p {
  color: rgba(255, 255, 255, 0.6);
  font-size: 0.9rem;
  margin-bottom: 16px;
}

.instruction-card ol,
.instruction-card ul {
  color: rgba(255, 255, 255, 0.7);
  padding-left: 20px;
  font-size: 0.9rem;
  line-height: 1.8;
}

.instruction-card strong {
  color: #ef4444;
}

/* 消息提示 */
.message-toast {
  position: fixed;
  bottom: 24px;
  right: 24px;
  padding: 16px 24px;
  border-radius: 12px;
  color: #fff;
  font-weight: 500;
  z-index: 1001;
  animation: slideIn 0.3s ease;
}

.message-toast.success {
  background: linear-gradient(135deg, #22c55e 0%, #16a34a 100%);
}

.message-toast.error {
  background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%);
}

.message-toast.info {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
}

@keyframes slideIn {
  from {
    transform: translateX(100px);
    opacity: 0;
  }
  to {
    transform: translateX(0);
    opacity: 1;
  }
}
</style>
